import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import './ReceiptDashboardView.css';

function ReceiptDashboardView({ isAdmin = false }) {
  const [stats, setStats] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [supportStaff, setSupportStaff] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedIndividual, setSelectedIndividual] = useState('');
  const [showQBModal, setShowQBModal] = useState(false);
  const [qbReceiptId, setQbReceiptId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const stats = await apiService.getReceiptStats();
      setStats(stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (selectedStaff) params.caretaker_id = selectedStaff;
      if (selectedIndividual) params.individual_id = selectedIndividual;
      
      const receipts = await apiService.getReceipts(params);
      setReceipts(receipts);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, selectedStaff, selectedIndividual]);

  const fetchFilters = useCallback(async () => {
    try {
      const [staffData, individualsData] = await Promise.all([
        apiService.getCaretakers(),
        apiService.request('/receipts/individuals')
      ]);
      setSupportStaff(staffData);
      setIndividuals(individualsData);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchFilters();
  }, [fetchStats, fetchFilters]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const handleApprove = async (receiptId) => {
    try {
      await apiService.approveReceipt(receiptId, 'Admin');
      fetchStats();
      fetchReceipts();
      if (selectedReceipt && selectedReceipt.id === receiptId) {
        setShowDetailModal(false);
        setSelectedReceipt(null);
      }
    } catch (error) {
      console.error('Error approving receipt:', error);
      alert('Failed to approve receipt');
    }
  };

  const handleReject = async (receiptId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await apiService.rejectReceipt(receiptId, 'Admin', reason);
      fetchStats();
      fetchReceipts();
      if (selectedReceipt && selectedReceipt.id === receiptId) {
        setShowDetailModal(false);
        setSelectedReceipt(null);
      }
    } catch (error) {
      console.error('Error rejecting receipt:', error);
      alert('Failed to reject receipt');
    }
  };

  const openReceiptDetail = (receipt) => {
    setSelectedReceipt(receipt);
    setShowDetailModal(true);
  };

  const handleQuickBooksSubmit = (receiptId) => {
    setQbReceiptId(receiptId);
    setShowQBModal(true);
  };

  const confirmQuickBooksSubmit = async () => {
    // For now, just update the receipt with a note that it was "sent" to QB
    try {
      await apiService.updateReceipt(qbReceiptId, {
        notes: 'Sent to QuickBooks (integration not yet implemented)'
      });
      setShowQBModal(false);
      setQbReceiptId(null);
      fetchReceipts(); // Refresh the list
    } catch (error) {
      console.error('Error marking receipt as sent to QuickBooks:', error);
    }
  };

  const handleDelete = async (receipt) => {
    try {
      await apiService.deleteReceipt(receipt.id);
      setDeleteConfirm(null);
      fetchStats();
      fetchReceipts();
      if (selectedReceipt && selectedReceipt.id === receipt.id) {
        setShowDetailModal(false);
        setSelectedReceipt(null);
      }
    } catch (error) {
      console.error('Error deleting receipt:', error);
      alert('Failed to delete receipt');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-badge approved';
      case 'pending':
        return 'status-badge pending';
      case 'flagged':
        return 'status-badge flagged';
      case 'rejected':
        return 'status-badge rejected';
      default:
        return 'status-badge';
    }
  };

  // Group receipts by support staff
  const groupByStaff = (receipts) => {
    const grouped = {};
    receipts.forEach(receipt => {
      const staffName = receipt.caretaker.name;
      if (!grouped[staffName]) {
        grouped[staffName] = {
          staff: receipt.caretaker,
          receipts: [],
          total: 0
        };
      }
      grouped[staffName].receipts.push(receipt);
      grouped[staffName].total += receipt.total_amount;
    });
    return grouped;
  };

  const groupedByStaff = groupByStaff(receipts);

  return (
    <div className="dashboard-view">
      {/* Stats Cards */}
      {stats && (
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-number">{stats.total_receipts}</div>
              <div className="stat-label">Total Receipts</div>
            </div>
            <div className="stat-card pending-card">
              <div className="stat-icon">⏳</div>
              <div className="stat-number">{stats.pending_count}</div>
              <div className="stat-label">Pending Approval</div>
            </div>
            <div className="stat-card flagged-card">
              <div className="stat-icon">⚠️</div>
              <div className="stat-number">{stats.flagged_count}</div>
              <div className="stat-label">Flagged</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-number">${stats.total_week.toFixed(2)}</div>
              <div className="stat-label">This Week</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📆</div>
              <div className="stat-number">${stats.total_month.toFixed(2)}</div>
              <div className="stat-label">This Month</div>
            </div>
            <div className="stat-card approved-card">
              <div className="stat-icon">✓</div>
              <div className="stat-number">{stats.approved_count}</div>
              <div className="stat-label">Approved</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="controls-section">
        <div className="filter-group">
          <label>Status:</label>
          <div className="filter-buttons">
            <button
              className={filter === 'all' ? 'filter-btn active' : 'filter-btn'}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={filter === 'pending' ? 'filter-btn active' : 'filter-btn'}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
            <button
              className={filter === 'approved' ? 'filter-btn active' : 'filter-btn'}
              onClick={() => setFilter('approved')}
            >
              Approved
            </button>
            <button
              className={filter === 'flagged' ? 'filter-btn active' : 'filter-btn'}
              onClick={() => setFilter('flagged')}
            >
              Flagged
            </button>
          </div>
        </div>

        <div className="filter-group">
          <label>Support Staff:</label>
          <select value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)}>
            <option value="">All Staff</option>
            {supportStaff.map((staff) => (
              <option key={staff.id} value={staff.id}>{staff.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Individual:</label>
          <select value={selectedIndividual} onChange={(e) => setSelectedIndividual(e.target.value)}>
            <option value="">All Individuals</option>
            {individuals.map((individual) => (
              <option key={individual.id} value={individual.id}>{individual.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Receipts Display */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading receipts...</p>
        </div>
      ) : receipts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>No receipts found</h3>
          <p>Receipts will appear here when support staff submit them</p>
        </div>
      ) : (
        <div className="receipts-by-staff">
          {Object.entries(groupedByStaff).map(([staffName, data]) => (
            <div key={staffName} className="staff-group">
              <div className="staff-group-header">
                <div className="staff-info-header">
                  <div className="staff-avatar-small">
                    {staffName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3>{staffName}</h3>
                    <p>{data.receipts.length} receipt{data.receipts.length !== 1 ? 's' : ''} • ${data.total.toFixed(2)} total</p>
                  </div>
                </div>
              </div>

              <div className="receipts-grid">
                {data.receipts.map((receipt) => (
                  <div
                    key={receipt.id}
                    className="receipt-card"
                    onClick={() => openReceiptDetail(receipt)}
                  >
                    <div className="receipt-header">
                      <div className="receipt-store">
                        <span className="store-icon">🏪</span>
                        <span className="store-name">{receipt.store_name}</span>
                      </div>
                      <span className={getStatusBadgeClass(receipt.status)}>
                        {receipt.status}
                      </span>
                    </div>

                    <div className="receipt-body">
                      <div className="receipt-amount">
                        ${receipt.total_amount.toFixed(2)} {receipt.currency}
                      </div>
                      <div className="receipt-datetime">
                        {formatDate(receipt.purchase_date)} at {formatTime(receipt.purchase_date)}
                      </div>
                    </div>

                    {receipt.individual && (
                      <div className="receipt-individual">
                        <span className="individual-icon">🤝</span>
                        <span>For: {receipt.individual.name}</span>
                      </div>
                    )}

                    {receipt.flag_reason && (
                      <div className="flag-indicator">
                        ⚠️ {receipt.flag_reason}
                      </div>
                    )}

                    {receipt.status === 'pending' && (
                      <div className="receipt-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="action-btn approve-btn"
                          onClick={() => handleApprove(receipt.id)}
                        >
                          ✓ Approve
                        </button>
                        <button
                          className="action-btn reject-btn"
                          onClick={() => handleReject(receipt.id)}
                        >
                          ✗ Reject
                        </button>
                      </div>
                    )}

                    {receipt.status === 'approved' && !receipt.notes?.includes('Sent to QuickBooks') && (
                      <div className="receipt-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="action-btn quickbooks-btn"
                          onClick={() => handleQuickBooksSubmit(receipt.id)}
                        >
                          📊 Submit to QuickBooks
                        </button>
                      </div>
                    )}

                    {/* Delete button (admin only, bottom-left) */}
                    {isAdmin && (
                      <button
                        className="delete-receipt-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(receipt);
                        }}
                        title="Delete receipt"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QuickBooks Modal */}
      {showQBModal && (
        <div className="modal-overlay" onClick={() => setShowQBModal(false)}>
          <div className="modal-content qb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>QuickBooks Integration</h2>
              <button className="modal-close" onClick={() => setShowQBModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="qb-message">
                <div className="qb-icon">📊</div>
                <h3>Integration Not Yet Implemented</h3>
                <p>
                  The QuickBooks integration is currently under development. 
                  This receipt will be marked as "ready for QuickBooks" but will not be automatically exported.
                </p>
                <p className="qb-note">
                  <strong>Note:</strong> Once clicked, this receipt will be marked as processed 
                  and the QuickBooks button will be removed from the receipt card.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-btn btn-secondary" onClick={() => setShowQBModal(false)}>
                Cancel
              </button>
              <button className="modal-btn qb-confirm-btn" onClick={confirmQuickBooksSubmit}>
                Mark as Processed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Detail Modal */}
      {showDetailModal && selectedReceipt && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Receipt Details</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Store:</span>
                <span className="detail-value">{selectedReceipt.store_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Amount:</span>
                <span className="detail-value">
                  ${selectedReceipt.total_amount.toFixed(2)} {selectedReceipt.currency}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Purchase Date:</span>
                <span className="detail-value">
                  {formatDateTime(selectedReceipt.purchase_date)}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Submitted By:</span>
                <span className="detail-value">{selectedReceipt.caretaker.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{selectedReceipt.caretaker.phone_number}</span>
              </div>
              {selectedReceipt.individual && (
                <div className="detail-row">
                  <span className="detail-label">Individual:</span>
                  <span className="detail-value">
                    {selectedReceipt.individual.name} ({selectedReceipt.individual.patient_id})
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className={getStatusBadgeClass(selectedReceipt.status)}>
                  {selectedReceipt.status}
                </span>
              </div>
              {selectedReceipt.flag_reason && (
                <div className="detail-row">
                  <span className="detail-label">Flag Reason:</span>
                  <span className="detail-value flag-warning">
                    ⚠️ {selectedReceipt.flag_reason}
                  </span>
                </div>
              )}
              {selectedReceipt.notes && (
                <div className="detail-row">
                  <span className="detail-label">Notes:</span>
                  <span className="detail-value">{selectedReceipt.notes}</span>
                </div>
              )}
              {selectedReceipt.approved_by && (
                <div className="detail-row">
                  <span className="detail-label">Approved By:</span>
                  <span className="detail-value">
                    {selectedReceipt.approved_by} on {formatDate(selectedReceipt.approved_at)}
                  </span>
                </div>
              )}

              <div className="receipt-image-container">
                <img
                  src={apiService.getReceiptImageUrl(selectedReceipt.id)}
                  crossOrigin="use-credentials"
                  alt="Receipt"
                  className="receipt-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="receipt-image-placeholder" style={{display: 'none'}}>
                  <div className="placeholder-icon">📄</div>
                  <p>Receipt Image</p>
                  <small>{selectedReceipt.image_path}</small>
                </div>
              </div>
            </div>

            {selectedReceipt.status === 'pending' && (
              <div className="modal-footer">
                <button
                  className="modal-btn approve-btn"
                  onClick={() => handleApprove(selectedReceipt.id)}
                >
                  ✓ Approve Receipt
                </button>
                <button
                  className="modal-btn reject-btn"
                  onClick={() => handleReject(selectedReceipt.id)}
                >
                  ✗ Reject Receipt
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Deletion</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="warning-icon">⚠️</div>
              <p>Are you sure you want to delete this receipt?</p>
              <div className="delete-details">
                <p><strong>{deleteConfirm.store_name}</strong></p>
                <p>${deleteConfirm.total_amount.toFixed(2)} {deleteConfirm.currency}</p>
                <p className="detail-text">{formatDate(deleteConfirm.purchase_date)}</p>
              </div>
              <p className="warning-text">
                This action cannot be undone. The receipt image will also be permanently deleted.
              </p>
            </div>

            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                <span>🗑️</span> Delete Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceiptDashboardView;

